import {AfterViewInit, Directive, ElementRef, NgZone, OnDestroy} from '@angular/core';

/**
 * Deja la cabecera de una tabla larga siempre a la vista: las filas se desplazan dentro del contenedor
 * de la tabla y no con la página.
 *
 * La cabecera no puede quedarse fija con la página, porque el contenedor ya desplaza la tabla en
 * horizontal (overflow-x) y eso lo convierte en el contenedor de scroll de todo lo que lleva dentro: un
 * `position: sticky` se pega a él, no a la pantalla. Así que es el contenedor el que se desplaza también
 * en vertical (con overflow-x: auto, el navegador ya le pone overflow-y: auto), con un alto máximo que,
 * con la página arriba del todo, lo hace acabar al pie de la ventana dejando sitio para lo que venga
 * justo detrás de él (el paginador). La cabecera se fija con `sticky: true` en el `matHeaderRowDef` de
 * cada tabla. De paso, la barra de scroll horizontal queda siempre a la vista.
 *
 * Lo que va detrás de la tabla en la página (paginador, relleno de los contenedores, pie de página) se
 * descuenta para que la página no vuelva a tener scroll.
 *
 * El alto se recalcula al cambiar el tamaño de la ventana o de lo que hay alrededor de la tabla (filtros
 * que bajan a otra línea, la tarjeta de "cargando" que se va...). Nunca baja de ALTO_MINIMO_PX: si lo de
 * encima ocupa casi toda la pantalla, se desplaza la página para llegar a la tabla.
 *
 * Dentro de un diálogo no hace nada: el diálogo ya tiene su propio scroll y su alto depende de lo que
 * lleva dentro.
 */
@Directive({
    selector: '[cabeceraFija]'
})
export class CabeceraFijaDirective implements AfterViewInit, OnDestroy {
    public static readonly ALTO_MINIMO_PX = 300;
    /** Aire entre lo último de la página y el borde inferior de la ventana */
    public static readonly MARGEN_INFERIOR_PX = 16;

    private _observador: ResizeObserver | null = null;
    private _pendiente: number | null = null;

    constructor(private _el: ElementRef<HTMLElement>, private _zone: NgZone) {
    }

    ngAfterViewInit(): void {
        if (this._el.nativeElement.closest('.mat-dialog-container')) {
            return;
        }
        this._zone.runOutsideAngular(() => {
            window.addEventListener('resize', this._programar);
            if (typeof ResizeObserver !== 'undefined') {
                // Lo que cambia de alto encima de la tabla la mueve, y lo que va detrás (el paginador, la
                // tarjeta de "cargando") cambia el sitio que hay que dejarle. Cualquiera de los dos cambia
                // el alto de alguno de los contenedores de la tabla; se vigilan todos porque los de fuera
                // pueden tener alto fijo y no enterarse (el <main> ocupa siempre la pantalla entera).
                this._observador = new ResizeObserver(this._programar);
                for (let padre = this._el.nativeElement.parentElement; padre; padre = padre.parentElement) {
                    this._observador.observe(padre);
                }
            }
            this.recalcular();
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener('resize', this._programar);
        this._observador?.disconnect();
        if (this._pendiente !== null) {
            cancelAnimationFrame(this._pendiente);
        }
    }

    /** Ajusta el alto máximo del contenedor para que acabe al pie de la ventana */
    recalcular(): void {
        const el = this._el.nativeElement;
        // Oculto (una pestaña que no se ve): no hay nada que medir
        if (!el.isConnected || el.offsetParent === null) {
            return;
        }
        const caja = el.getBoundingClientRect();
        const alto = Math.max(CabeceraFijaDirective.ALTO_MINIMO_PX, Math.floor(
            this.altoVentana() - this._arribaEnLaPagina(caja.top)
            - this._altoDetras(caja.bottom) - CabeceraFijaDirective.MARGEN_INFERIOR_PX));
        if (el.style.maxHeight !== `${alto}px`) {
            el.style.maxHeight = `${alto}px`;
        }
    }

    /** Lo que se ve de la página, sin la barra de scroll horizontal (separado para poder fijarlo en los tests) */
    protected altoVentana(): number {
        return document.documentElement.clientHeight;
    }

    /**
     * Dónde empieza la tabla con la página arriba del todo: así no depende de cuánto se haya bajado. Se
     * suma lo desplazado en cada contenedor (el último es <html>, que es lo desplazado en la ventana) y
     * no solo en la ventana: en el admin el contenido del sidenav tiene overflow: auto, aunque hoy crece
     * con la página y es el documento el que se desplaza.
     */
    private _arribaEnLaPagina(arribaEnPantalla: number): number {
        let desplazado = 0;
        for (let padre = this._el.nativeElement.parentElement; padre; padre = padre.parentElement) {
            desplazado += padre.scrollTop;
        }
        return arribaEnPantalla + desplazado;
    }

    /**
     * Lo que va detrás del contenedor hasta el final de la página: los hermanos (el paginador), con sus
     * márgenes, y, subiendo por los contenedores, su relleno inferior y lo que va detrás de cada uno (en
     * el cliente, el <footer> de la aplicación y el relleno del <main>). Sin esto la página vuelve a tener
     * scroll y la tabla queda con dos barras. Lo que va fijo o flotando (el menú lateral) no ocupa sitio.
     */
    private _altoDetras(abajoTabla: number): number {
        let abajo = abajoTabla;
        for (let hermano = this._el.nativeElement.nextElementSibling; hermano; hermano = hermano.nextElementSibling) {
            const caja = hermano.getBoundingClientRect();
            if (caja.height > 0) {
                abajo = Math.max(abajo, caja.bottom + parseFloat(getComputedStyle(hermano).marginBottom || '0'));
            }
        }

        let fuera = 0;
        for (let padre = this._el.nativeElement.parentElement; padre && padre !== document.body; padre = padre.parentElement) {
            const estilo = getComputedStyle(padre);
            fuera += (parseFloat(estilo.paddingBottom) || 0) + (parseFloat(estilo.borderBottomWidth) || 0);
            for (let hermano = padre.nextElementSibling; hermano; hermano = hermano.nextElementSibling) {
                fuera += CabeceraFijaDirective._altoEnElFlujo(hermano);
            }
        }
        return abajo - abajoTabla + fuera;
    }

    /** Lo que ocupa un elemento en la página, con sus márgenes; 0 si va fijo, flotando o no se ve */
    private static _altoEnElFlujo(el: Element): number {
        const estilo = getComputedStyle(el);
        if (estilo.position === 'fixed' || estilo.position === 'absolute' || estilo.display === 'none') {
            return 0;
        }
        const alto = el.getBoundingClientRect().height;
        return alto > 0 ? alto + (parseFloat(estilo.marginTop) || 0) + (parseFloat(estilo.marginBottom) || 0) : 0;
    }

    /** Agrupa los avisos de un mismo fotograma; además evita el "ResizeObserver loop" del navegador */
    private _programar = () => {
        if (this._pendiente === null) {
            this._pendiente = requestAnimationFrame(() => {
                this._pendiente = null;
                this.recalcular();
            });
        }
    };
}
